import React, { useMemo, useState } from 'react';
import PageHeader from '../../components/shared/PageHeader';
import DataToolbar, { StatusPill, Card } from '../../components/shared/DataToolbar';
import { LoadingState, ErrorState, EmptyResults } from '../../components/shared/Pending';
import { CalendarDays, CreditCard, Loader2, MapPin, UsersRound } from 'lucide-react';
import { useApi } from '../../api/useApi';
import { getAdminBooking, listAdminBookings } from '../../api/adminApi';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';

const money = (value, currency = 'USD') => {
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(Number(value || 0));
  } catch (_) {
    return `$${Number(value || 0).toFixed(2)}`;
  }
};

const dateTime = (value) => value
  ? new Date(value).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })
  : '—';

export default function AdminBookings() {
  const { data, loading, error, refetch } = useApi(() => listAdminBookings({ per_page: 100 }), []);
  const rows = useMemo(() => Array.isArray(data) ? data : (data?.items || data || []), [data]);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const filtered = rows.filter((booking) => {
    const haystack = `${booking.id} ${booking.reference || ''} ${booking.customer?.name || booking.guest?.name || ''} ${booking.customer?.email || booking.guest?.email || ''} ${booking.property?.title || ''} ${booking.property?.host?.name || ''}`.toLowerCase();
    const matchesQuery = !q || haystack.includes(q.toLowerCase());
    const matchesStatus = !status || (booking.status || '').toLowerCase() === status.toLowerCase().replace(' ', '_');
    return matchesQuery && matchesStatus;
  });

  const openBooking = async (booking) => {
    setSelected(booking);
    setDetailLoading(true);
    try {
      const detail = await getAdminBooking(booking.id);
      setSelected(detail || booking);
    } catch (_) {
      setSelected(booking);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div>
      <PageHeader title="Bookings" description="All reservations across StayEasy." />
      <DataToolbar
        search={q}
        onSearch={setQ}
        placeholder="Search booking, guest, property or host…"
        filters={[{ label: 'Status', value: status, options: ['All', 'Pending payment', 'Confirmed', 'Completed', 'Cancelled'], onChange: setStatus }]}
      />

      {loading ? <LoadingState label="Loading bookings…" />
        : error ? <ErrorState error={error} onRetry={refetch} />
        : filtered.length === 0 ? <EmptyResults icon={CalendarDays} title="No bookings found" description="No marketplace bookings match these filters." />
        : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left font-medium px-4 py-3">Booking</th>
                    <th className="text-left font-medium px-4 py-3">Property</th>
                    <th className="text-left font-medium px-4 py-3">Host</th>
                    <th className="text-left font-medium px-4 py-3">Guest</th>
                    <th className="text-left font-medium px-4 py-3">Dates</th>
                    <th className="text-right font-medium px-4 py-3">Total</th>
                    <th className="text-left font-medium px-4 py-3">Status</th>
                    <th className="text-left font-medium px-4 py-3">Payment</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((booking) => (
                    <tr key={booking.id} onClick={() => openBooking(booking)} className="cursor-pointer hover:bg-slate-50">
                      <td className="px-4 py-3">
                        <div className="font-mono text-xs text-slate-700">#{booking.id}</div>
                        <div className="text-[11px] text-slate-400 mt-0.5">{booking.reference}</div>
                      </td>
                      <td className="px-4 py-3"><div className="font-medium text-slate-900 max-w-[220px] truncate">{booking.property?.title || '—'}</div></td>
                      <td className="px-4 py-3 text-slate-700">{booking.property?.host?.name || '—'}</td>
                      <td className="px-4 py-3">
                        <div className="text-slate-900">{booking.customer?.name || booking.guest?.name || '—'}</div>
                        <div className="text-xs text-slate-400">{booking.customer?.email || booking.guest?.email || ''}</div>
                      </td>
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">{booking.check_in || '—'} → {booking.check_out || '—'} <span className="text-slate-400 text-xs">{booking.nights ? `(${booking.nights}n)` : ''}</span></td>
                      <td className="px-4 py-3 text-right font-semibold tabular-nums whitespace-nowrap">{money(booking.total_amount, booking.currency)}</td>
                      <td className="px-4 py-3"><StatusPill status={booking.status} /></td>
                      <td className="px-4 py-3"><StatusPill status={booking.transaction?.status || (booking.paid_at ? 'paid' : 'pending')} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

      <BookingDetail booking={selected} loading={detailLoading} onOpenChange={(open) => { if (!open) setSelected(null); }} />
    </div>
  );
}

function BookingDetail({ booking, loading, onOpenChange }) {
  const guest = booking?.guest || booking?.customer || {};
  return (
    <Dialog open={Boolean(booking)} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto p-0">
        {booking && (
          <>
            <div className="border-b border-slate-200 p-6">
              <DialogHeader>
                <div className="flex items-center justify-between gap-4">
                  <DialogTitle>Booking {booking.reference || `#${booking.id}`}</DialogTitle>
                  {loading && <span className="inline-flex items-center gap-2 text-xs text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading details</span>}
                </div>
              </DialogHeader>
              <div className="mt-3 flex flex-wrap gap-2"><StatusPill status={booking.status} /><StatusPill status={booking.transaction?.status || (booking.paid_at ? 'paid' : 'pending')} /></div>
            </div>
            <div className="grid md:grid-cols-2">
              <Section icon={CalendarDays} title="Reservation">
                <Item label="Booking ID" value={booking.reference || booking.id} mono />
                <Item label="Created" value={dateTime(booking.created_at)} />
                <Item label="Check-in" value={booking.check_in} />
                <Item label="Check-out" value={booking.check_out} />
                <Item label="Nights / guests" value={`${booking.nights || 0} nights · ${booking.guests || 1} guests`} />
              </Section>
              <Section icon={UsersRound} title="Guest">
                <Item label="Primary guest" value={guest.name} />
                <Item label="Email" value={guest.email} />
                <Item label="Phone" value={guest.phone} />
                <Item label="Additional guests" value={(booking.guest?.additional_names || []).join(', ') || 'None listed'} />
              </Section>
              <Section icon={MapPin} title="Property & host">
                <Item label="Property" value={booking.property?.title} />
                <Item label="Location" value={booking.property?.location} />
                <Item label="Host" value={booking.property?.host?.name} />
                <Item label="Property ID" value={booking.property?.id} mono />
              </Section>
              <Section icon={CreditCard} title="Payment & references">
                <Item label="Total" value={money(booking.total_amount, booking.currency)} />
                <Item label="Transaction ID" value={booking.transaction?.transaction_id} mono />
                <Item label="Invoice" value={booking.transaction?.invoice_number} mono />
                <Item label="Stripe Payment Intent" value={booking.stripe_payment_intent_id} mono />
                <Item label="Stripe Checkout Session" value={booking.stripe_checkout_session_id} mono />
                <Item label="Paid" value={dateTime(booking.paid_at || booking.transaction?.paid_at)} />
              </Section>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

const Section = ({ icon: Icon, title, children }) => (
  <section className="border-b border-slate-200 p-6 md:border-r">
    <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-slate-500"><Icon className="h-4 w-4" /> {title}</h3>
    <div className="mt-4 space-y-4">{children}</div>
  </section>
);

const Item = ({ label, value, mono }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
    <div className={`mt-1 break-words text-sm text-slate-900 ${mono ? 'font-mono text-xs' : 'font-medium'}`}>{value || '—'}</div>
  </div>
);
